import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { GeneratedColumnSkeletonCell } from "../../../container/Enrichment/GeneratedColumnLoading";
import {
  AI_SUMMARY_PREVIEW_LIMIT,
  formatEnrichmentHistoryResult,
} from "./companyEnrichmentFormat";
import { SIDEBAR } from "./sidebarVisual";

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 44;

const columnsSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.7fr)",
  columnGap: 0,
  alignItems: "stretch",
  px: SIDEBAR.cardPadX,
  boxSizing: "border-box",
};

function PromptCell({ children }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: 550,
        color: "text.primary",
        lineHeight: 1.25,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        alignSelf: "center",
      }}
    >
      {children}
    </Typography>
  );
}

PromptCell.propTypes = {
  children: PropTypes.node,
};

function ResultCellContent({ entry }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const formatted = formatEnrichmentHistoryResult(entry.resultPayload || { status: entry.status, actionType: entry.actionType });

  if (formatted.loading) {
    return (
      <Box
        data-testid={`ai-enrichment-loading-${entry.id}`}
        sx={{
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          minHeight: ROW_HEIGHT - 12,
          py: 0.5,
          px: 0.5,
          borderRadius: "6px",
          bgcolor: theme.palette.mode === "dark" ? "rgba(74, 44, 245, 0.12)" : theme.palette.secondary?.main || "#F6F4FF",
        }}
      >
        <GeneratedColumnSkeletonCell
          variant={formatted.variant}
          data-testid={`generated-column-skeleton-${entry.id}`}
        />
      </Box>
    );
  }

  const bodyText = formatted.text ? String(formatted.text) : "—";
  const needsClamp = Boolean(formatted.expandable && bodyText.length > AI_SUMMARY_PREVIEW_LIMIT);
  const visibleBody =
    needsClamp && !expanded
      ? `${bodyText.slice(0, AI_SUMMARY_PREVIEW_LIMIT).trim()}…`
      : bodyText;

  if (formatted.isEmail && formatted.email) {
    return (
      <Link
        href={`mailto:${formatted.email}`}
        underline="hover"
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: "primary.main",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          alignSelf: "center",
        }}
      >
        {formatted.email}
      </Link>
    );
  }

  return (
    <Box sx={{ alignSelf: "center", minWidth: 0 }}>
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: formatted.tone === "error" ? 500 : 400,
          color: formatted.tone === "error" ? "error.main" : "text.primary",
          lineHeight: 1.35,
          whiteSpace: needsClamp && expanded ? "pre-wrap" : "normal",
          wordBreak: "break-word",
          overflow: "hidden",
          display: needsClamp && !expanded ? "-webkit-box" : "block",
          WebkitLineClamp: needsClamp && !expanded ? 3 : "unset",
          WebkitBoxOrient: "vertical",
        }}
      >
        {visibleBody.split("\n").map((line, index) => (
          <Box
            component="span"
            display="block"
            key={`${entry.id}-result-line-${index}`}
          >
            {line}
          </Box>
        ))}
      </Typography>
      {needsClamp ? (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setExpanded((value) => !value)}
          sx={{
            mt: 0.25,
            fontSize: 12.5,
            fontWeight: 600,
            color: "primary.main",
            cursor: "pointer",
            display: "block",
          }}
        >
          {expanded ? "See less" : "See more"}
        </Link>
      ) : null}
    </Box>
  );
}

ResultCellContent.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    actionType: PropTypes.string,
    status: PropTypes.string,
    resultPayload: PropTypes.object,
  }).isRequired,
};

function CompactHistoryRow({ entry }) {
  return (
    <Box
      data-testid={`ai-enrichment-compact-row-${entry.id}`}
      sx={{
        borderRadius: SIDEBAR.radius,
        border: SIDEBAR.border,
        bgcolor: SIDEBAR.surface,
        boxShadow: SIDEBAR.shadow,
        px: SIDEBAR.cardPadX,
        py: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.75 }}>
        {entry.prompt}
      </Typography>
      {entry.source?.label ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.75 }}>
          {entry.source.label}
        </Typography>
      ) : null}
      <ResultCellContent entry={entry} />
    </Box>
  );
}

CompactHistoryRow.propTypes = {
  entry: PropTypes.object.isRequired,
};

/**
 * AI / enrichment action history for Product Details (Prompt | Result | Source).
 * Desktop matches PeopleTableV2 chrome; compact uses stacked cards.
 */
export default function AiEnrichmentHistoryTable({ entries = [], compact = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const headerBg = isDark ? "#2A2A2A" : "#F9FAFB";
  const stripeBg = isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
  const divider = theme.palette.divider;

  if (compact) {
    return (
      <Box
        data-testid="ai-enrichment-history-table"
        data-compact="true"
        sx={{ display: "flex", flexDirection: "column", gap: SIDEBAR.gap.listItem }}
      >
        {entries.map((entry) => (
          <CompactHistoryRow key={entry.id} entry={entry} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      data-testid="ai-enrichment-history-table"
      sx={{
        width: "100%",
        borderRadius: SIDEBAR.radius,
        border: SIDEBAR.border,
        overflow: "hidden",
        bgcolor: SIDEBAR.surface,
        boxShadow: SIDEBAR.shadow,
      }}
    >
      <Box
        role="row"
        aria-label="AI enrichment column headers"
        sx={{
          ...columnsSx,
          flexShrink: 0,
          minHeight: HEADER_HEIGHT,
          maxHeight: HEADER_HEIGHT,
          backgroundColor: headerBg,
          color: "text.secondary",
          borderBottom: `1px solid ${divider}`,
          alignItems: "center",
        }}
      >
        {["Prompt", "Result", "Source"].map((label) => (
          <Typography
            key={label}
            component="span"
            sx={{ fontWeight: 700, fontSize: 13, color: "inherit", lineHeight: 1.2 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box>
        {entries.map((entry, index) => (
          <Box
            key={entry.id}
            role="row"
            data-testid={`ai-enrichment-history-row-${entry.id}`}
            data-status={entry.status}
            sx={{
              ...columnsSx,
              minHeight: ROW_HEIGHT,
              py: 0.75,
              cursor: "default",
              borderBottom: `1px solid ${divider}`,
              backgroundColor:
                index % 2 === 1 ? stripeBg : theme.palette.background.paper,
              "&:last-child": {
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <PromptCell>{entry.prompt}</PromptCell>
            <ResultCellContent entry={entry} />
            <Typography
              component="span"
              sx={{
                alignSelf: "center",
                color: "text.secondary",
                fontSize: 12.5,
                lineHeight: 1.25,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {entry.source?.label || "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

AiEnrichmentHistoryTable.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      prompt: PropTypes.string.isRequired,
      actionType: PropTypes.string,
      status: PropTypes.oneOf(["running", "success", "failed", "missing"]),
      resultPayload: PropTypes.object,
      source: PropTypes.shape({ label: PropTypes.string }),
    })
  ),
  compact: PropTypes.bool,
};
